const TRANSLATED_TAB_TITLE_PREFIX = "__tab_i18n__:";

export const buildId = (...parts) =>
  parts
    .filter((part) => part !== undefined && part !== null)
    .map((part) => String(part).trim())
    .filter(Boolean)
    .join(":");

export const makeTab = ({
  descriptor,
  type = descriptor?.type,
  idParts,
  title,
  params = {},
}) => ({
  id: buildId(...idParts),
  title,
  type,
  params,
});

export const trimToNull = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

export const ensureTabTitle = (tab, fallbackTitle = null) => {
  if (!tab) return null;

  const title = trimToNull(tab.title) || trimToNull(fallbackTitle) || tab.id;
  return title === tab.title ? tab : { ...tab, title };
};

export const withTitleOverride = (tab, titleOverride, fallbackTitle = null) =>
  ensureTabTitle(
    titleOverride ? { ...tab, title: titleOverride } : tab,
    fallbackTitle,
  );

export const decodeIdSegment = (value) => {
  try {
    return decodeURIComponent(String(value).replace(/\+/g, "%20"));
  } catch {
    return value;
  }
};

export const decodeBase64Url = (value) => {
  if (!value) return null;

  try {
    const normalized = String(value).replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
};

export const encodeBase64Url = (value) => {
  try {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  } catch {
    return null;
  }
};

export const encodeTranslatedTabTitle = ({ key, params = {} }) => {
  const encoded = encodeBase64Url(
    JSON.stringify({
      key,
      params,
    }),
  );

  return encoded ? `${TRANSLATED_TAB_TITLE_PREFIX}${encoded}` : key;
};

const decodeTranslatedTabTitle = (title) => {
  if (
    typeof title !== "string" ||
    !title.startsWith(TRANSLATED_TAB_TITLE_PREFIX)
  ) {
    return null;
  }

  const encoded = title.slice(TRANSLATED_TAB_TITLE_PREFIX.length);
  const decoded = decodeBase64Url(encoded);
  if (!decoded) return null;

  try {
    const parsed = JSON.parse(decoded);
    if (!parsed?.key) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const resolveTabTitle = (title, t) => {
  const translatedTitle = decodeTranslatedTabTitle(title);

  if (!translatedTitle) {
    return t(title);
  }

  return t(translatedTitle.key, translatedTitle.params);
};
